/**
 * Project Detail → Timeline tab → LIST view.
 *
 * Mirrors the mobile `src/features/projects/tabs/TaskTab.tsx`
 * rendering: a vertical timeline with a dot rail on the left and
 * one card per task on the right. Same data as the Calendar
 * (Gantt) view — both read from the org-wide `tasks` collection
 * through `useProjectTasks`.
 *
 * Sort order: `startDate` ascending; tasks without a start date
 * sink to the bottom (matches mobile).
 *
 * Click a row → opens the existing `TaskFormSlideOver` in edit
 * mode via the caller's `onEditTask(task)` prop (TimelineTab
 * already owns that form mount).
 */
import { useMemo } from 'react';

import {
  taskStatusLabel,
  type TaskItem,
} from '@/lib/useProjectTasks';

// Mirror the Calendar view's category metadata so the pill colour
// + label stay consistent between the two views.
const CATEGORY_LABELS: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  woodwork: 'Woodwork',
  design: 'Design',
  cleaning: 'Cleaning',
  floor_cleaning: 'Floor Cleaning',
  floor_matting: 'Floor Matting',
  painting: 'Painting',
  false_ceiling: 'False Ceiling',
  tiling: 'Tiling',
  general: 'General',
};

const CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  electrical: { bg: '#DBEAFE', fg: '#1D4ED8' },
  plumbing: { bg: '#D1FAE5', fg: '#065F46' },
  woodwork: { bg: '#FEF3C7', fg: '#92400E' },
  design: { bg: '#FCE7F3', fg: '#BE185D' },
  cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_matting: { bg: '#ECFCCB', fg: '#3F6212' },
  painting: { bg: '#FFEDD5', fg: '#9A3412' },
  false_ceiling: { bg: '#FFEDD5', fg: '#9A3412' },
  tiling: { bg: '#FEE2E2', fg: '#B91C1C' },
  general: { bg: '#F3F4F6', fg: '#374151' },
};

function categoryLabel(key: string | undefined): string {
  if (!key) return 'General';
  return CATEGORY_LABELS[key] ?? key.replace(/_/g, ' ');
}

function categoryColor(key: string | undefined): { bg: string; fg: string } {
  return CATEGORY_COLORS[key ?? ''] ?? CATEGORY_COLORS.general;
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function TimelineListView({
  tasks,
  onEditTask,
  onPostUpdate,
}: {
  tasks: TaskItem[];
  onEditTask: (task: TaskItem) => void;
  /** Optional. When set, each row shows a "+ Log work" button that
   *  opens the LogWorkSlideOver for that task. */
  onPostUpdate?: (task: TaskItem) => void;
}) {
  // Sort by startDate asc, undated at the bottom (mobile parity).
  const sorted = useMemo(() => {
    const datedAsc = [...tasks].sort((a, b) => {
      const am = a.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
      const bm = b.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
      return am - bm;
    });
    return datedAsc;
  }, [tasks]);

  // Header roll-up (matches the mobile copy "X done · Y upcoming…").
  const counts = useMemo(() => {
    let done = 0;
    let ongoing = 0;
    let notStarted = 0;
    for (const t of tasks) {
      if (t.status === 'completed') done += 1;
      else if (t.status === 'ongoing') ongoing += 1;
      else notStarted += 1;
    }
    return { done, ongoing, notStarted };
  }, [tasks]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header — eyebrow row matching mobile TaskTab */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          background: '#fff',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Project Timeline · {tasks.length}{' '}
          {tasks.length === 1 ? 'milestone' : 'milestones'}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            marginTop: 4,
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span>
            <b style={{ color: 'var(--success)' }}>{counts.done}</b> done
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <span>
            <b style={{ color: 'var(--brand)' }}>{counts.ongoing}</b> ongoing
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <span>
            <b>{counts.notStarted}</b> not started
          </span>
        </div>
      </div>

      {/* List body */}
      <div style={{ padding: '20px 20px 24px' }}>
        <div style={{ position: 'relative' }}>
          {/* Connecting vertical line (positioned to align with dots
              — rail dots are 16px circles centred at x = 24). */}
          {sorted.length > 1 ? (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: 23,
                top: 8,
                bottom: 8,
                width: 2,
                background: 'var(--border)',
                borderRadius: 1,
              }}
            />
          ) : null}

          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {sorted.map((t) => {
              const done = t.status === 'completed';
              const sMs = t.startDate?.toMillis?.() ?? null;
              const eMs = t.endDate?.toMillis?.() ?? null;
              const cat = categoryColor(t.category);
              const pct = Math.max(0, Math.min(100, t.progress ?? 0));
              const pillClass =
                t.status === 'completed'
                  ? 'stage-completed'
                  : t.status === 'ongoing'
                    ? 'stage-ongoing'
                    : 'stage-pending';
              return (
                <li
                  key={t.id}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}
                >
                  {/* Rail dot */}
                  <div
                    aria-hidden
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: done ? 'var(--success)' : '#fff',
                      border: done
                        ? '2px solid var(--success)'
                        : '2px solid var(--text-tertiary)',
                      flexShrink: 0,
                      marginTop: 22,
                      marginLeft: 16,
                      zIndex: 1,
                      boxShadow: '0 0 0 4px #fff',
                    }}
                  />

                  {/* Card */}
                  <button
                    type="button"
                    onClick={() => onEditTask(t)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: '#fff',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'box-shadow 0.15s, border-color 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--text-tertiary)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 8px rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Eyebrow — date range */}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: sMs ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontStyle: sMs ? 'normal' : 'italic',
                      }}
                    >
                      {sMs ? (
                        <>
                          {fmtDate(sMs)} → {eMs ? fmtDate(eMs) : '—'}
                        </>
                      ) : (
                        'No dates set'
                      )}
                    </div>

                    {/* Row 1: title + category pill */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          lineHeight: 1.3,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {t.title}
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 4,
                          background: cat.bg,
                          color: cat.fg,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {categoryLabel(t.category)}
                      </span>
                    </div>

                    {/* Description (clipped to 2 lines) */}
                    {t.description ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {t.description}
                      </div>
                    ) : null}

                    {/* Row 3: progress + status pill */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginTop: 2,
                      }}
                    >
                      <div
                        className="spent-bar"
                        style={{ flex: 1, height: 6 }}
                      >
                        <div
                          className="spent-bar-fill"
                          style={{
                            width: `${pct}%`,
                            background:
                              t.status === 'completed'
                                ? 'var(--success)'
                                : t.status === 'ongoing'
                                  ? 'var(--brand)'
                                  : 'var(--border)',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: 36,
                          textAlign: 'right',
                        }}
                      >
                        {pct}%
                      </span>
                      <span className={`stage-pill ${pillClass}`}>
                        {taskStatusLabel(t.status)}
                      </span>
                    </div>

                    {/* Footer row: assigned-to (left) + Log work (right) */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        marginTop: 2,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-tertiary)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {t.assignedToName ? (
                          <>
                            <i className="ph ph-user" /> {t.assignedToName}
                          </>
                        ) : null}
                      </div>
                      {onPostUpdate ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPostUpdate(t);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onPostUpdate(t);
                            }
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--brand)',
                            padding: '4px 8px',
                            border: '1px solid var(--brand)',
                            borderRadius: 4,
                            cursor: 'pointer',
                          }}
                          title="Post a work log update"
                        >
                          <i className="ph-bold ph-plus" /> Log work
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
